import type { SessionV2 } from "@/types/auth"
import type { Timestamp } from "@/types/date"
import type { UpdateUserProfileResults, UserRef } from "@/types/user"

import { mtLocation } from "@/lib/mt-location"
import { baseApi } from "./base-api"

export interface VerifiedPhone {
  phone_number: string
  verified_at: Timestamp
}

export interface PhoneVerificationChallenge {
  challenge_token: string
  expires_at: Timestamp
}

export const phoneAuthApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    startPhoneSignIn: builder.mutation<
      PhoneVerificationChallenge,
      { phone_number: string; enable_phone_signup?: boolean }
    >({
      query: (data) => ({
        url: new URL("/api/auth-v2/phone-sign-in", mtLocation.origin).href,
        method: "post",
        data,
        skipAuthHandling: true,
        retries: 0,
      }),
    }),
    verifyPhoneSignIn: builder.mutation<
      { session: SessionV2; registration_occurred?: boolean },
      { challenge_token: string; otp: string }
    >({
      query: (data) => ({
        url: new URL("/api/auth-v2/verify-phone-sign-in", mtLocation.origin)
          .href,
        method: "post",
        data,
        skipAuthHandling: true,
        retries: 0,
      }),
    }),
    savePhoneSignupEmail: builder.mutation<UpdateUserProfileResults, string>({
      query: (email) => ({
        url: "/api/user_profile",
        method: "put",
        data: { user_profile: { email } },
        retries: 0,
      }),
    }),
    getVerifiedPhone: builder.query<
      { verified_phone?: VerifiedPhone },
      UserRef
    >({
      query: () => ({ url: "/api/phone_verifications" }),
      keepUnusedDataFor: 0,
    }),
    startPhoneVerification: builder.mutation<
      PhoneVerificationChallenge,
      string
    >({
      query: (phoneNumber) => ({
        url: "/api/phone_verifications",
        method: "post",
        data: { phone_number: phoneNumber },
      }),
    }),
    verifyAndAddPhone: builder.mutation<
      VerifiedPhone,
      { challenge_token: string; otp: string }
    >({
      query: (data) => ({
        url: "/api/phone_verifications/verify",
        method: "post",
        data,
      }),
    }),
  }),
})

export const {
  useStartPhoneSignInMutation,
  useVerifyPhoneSignInMutation,
  useSavePhoneSignupEmailMutation,
  useGetVerifiedPhoneQuery,
  useStartPhoneVerificationMutation,
  useVerifyAndAddPhoneMutation,
} = phoneAuthApi
